import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import PageShell from '../Components/ui/PageShell';
import DashboardCard from '../Components/ui/DashboardCard';
import DashboardDateFilter from '../Components/ui/DashboardDateFilter';
import AppModal from '../Components/ui/AppModal';
import DataTable from '../Components/ui/DataTable';
import { getDecryptedData } from '../utils/authStorage';
import {
  getTeamRequirementsCount,
  getRequirementDetailsWithAssignedUsers,
} from '../services/dashboardService';
import {
  computeTeamLeadDashboardStats,
  computeTeamLeadDashboardDrillDown,
} from '../utils/dashboardStats';
import { getDefaultDashboardDateRange } from '../utils/requirementDateFilter';
import { useLayout } from '../layout/AppLayout';
import DashboardSourceProfileSearch, {
  DashboardSourceProfileSearchBar,
  DashboardSourceProfileSearchResults,
} from '../Components/dashboard/DashboardSourceProfileSearch';

const DEFAULT_DATE_RANGE = getDefaultDashboardDateRange();

function TeamLeadDashboard() {
  const userId = getDecryptedData('Id');
  const { openFormModal } = useLayout();
  const [loadError, setLoadError] = useState('');
  const [assignedRequirements, setAssignedRequirements] = useState([]);
  const [teamCountResponse, setTeamCountResponse] = useState({});
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [drillDownModal, setDrillDownModal] = useState({
    show: false,
    title: '',
    rows: [],
    showInterviewDate: false,
  });

  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      try {
        setLoadError('');

        let reqCount = {};
        try {
          reqCount = await getTeamRequirementsCount(userId);
        } catch {
          reqCount = {};
        }

        let assignedReqs = [];
        try {
          assignedReqs = await getRequirementDetailsWithAssignedUsers(userId);
        } catch {
          assignedReqs = [];
        }

        setTeamCountResponse(reqCount || {});
        setAssignedRequirements(Array.isArray(assignedReqs) ? assignedReqs : []);
      } catch (error) {
        console.error('TL dashboard load failed:', error);
        setLoadError('Failed to load dashboard data.');
      }
    };
    load();
  }, [userId]);

  const stats = useMemo(
    () => computeTeamLeadDashboardStats(assignedRequirements, teamCountResponse, dateRange),
    [assignedRequirements, teamCountResponse, dateRange]
  );

  const drillDown = useMemo(
    () => computeTeamLeadDashboardDrillDown(assignedRequirements, teamCountResponse, dateRange),
    [assignedRequirements, teamCountResponse, dateRange]
  );

  const openDrillDownModal = ({ title, rows, showInterviewDate = false }) => {
    setDrillDownModal({
      show: true,
      title,
      rows: Array.isArray(rows) ? rows : [],
      showInterviewDate,
    });
  };

  const closeDrillDownModal = () => {
    setDrillDownModal({
      show: false,
      title: '',
      rows: [],
      showInterviewDate: false,
    });
  };

  const candidateColumns = useMemo(() => {
    const columns = [
      { key: 'candidateName', label: 'Source Profile', sortable: true },
      { key: 'client', label: 'Client', sortable: true },
      { key: 'role', label: 'Role', sortable: true },
      { key: 'regId', label: 'Req ID', sortable: true },
      { key: 'status', label: 'Status', sortable: true, type: 'status', useStoredLabel: true },
    ];

    if (drillDownModal.showInterviewDate) {
      columns.push({ key: 'interviewDateLabel', label: 'Date & Time', sortable: true });
    }

    columns.push({
      key: 'requirementId',
      label: 'Action',
      render: (row) => (
        <Link to="/team/profiles-view">
          View Profiles
        </Link>
      ),
    });

    return columns;
  }, [drillDownModal.showInterviewDate]);

  return (
    <DashboardSourceProfileSearch>
    <PageShell
      title="Team Lead Dashboard"
      subtitle="Your team overview — open Team Workspace for daily requirement and candidate work."
      breadcrumbs={[{ label: 'Dashboard', path: '/dashboard' }]}
      actions={<DashboardSourceProfileSearchBar />}
    >
      {loadError && <div className="alert alert-danger">{loadError}</div>}

      <DashboardDateFilter
        onApply={setDateRange}
        onReset={setDateRange}
      />

      <p className="hr-dashboard-filter-hint">
        Showing:
        {dateRange.from ? ` ${dateRange.from}` : ''}
        {dateRange.to ? ` → ${dateRange.to}` : ''}
        {' · '}
        reqs by start date, profiles by upload date
      </p>

      <DashboardSourceProfileSearchResults />

      <div className="hr-stat-grid">
        <DashboardCard title="Profiles View" value="View" icon="👁️" accent="teal" to="/team/profiles-view" subtitle="Source profiles by date" />
        <DashboardCard title="Assigned Requirements" value={stats.requirements} icon="📋" accent="orange" to="/team/requirements" />
        <DashboardCard
          title="Source Profiles Submitted"
          value={stats.candidates}
          icon="🧑‍💼"
          accent="teal"
          onClick={() => openDrillDownModal({
            title: 'Source Profiles Submitted',
            rows: drillDown.sourceProfiles,
          })}
        />
        <DashboardCard title="Status Tracking" value="View" icon="📊" accent="blue" to="/reports/tracking" subtitle="Pipeline by stage" />
        <DashboardCard
          title="Interviews"
          value={stats.interviews}
          icon="🎯"
          accent="blue"
          onClick={() => openDrillDownModal({
            title: 'Interviews',
            rows: drillDown.interviews,
            showInterviewDate: true,
          })}
        />
        <DashboardCard
          title="Offers"
          value={stats.offers}
          icon="📨"
          accent="green"
          onClick={() => openDrillDownModal({
            title: 'Offers',
            rows: drillDown.offers,
          })}
        />
        <DashboardCard
          title="Joined"
          value={stats.joined}
          icon="✅"
          accent="green"
          onClick={() => openDrillDownModal({
            title: 'Joined',
            rows: drillDown.joined,
          })}
        />
      </div>

      <section className="hr-card">
        <h3 className="hr-section-title">Quick Actions</h3>
        <div className="hr-dashboard-grid">
          <button
            type="button"
            className="hr-dashboard-card hr-dashboard-card-btn"
            onClick={() => openFormModal('create-tl-requirement')}
          >
            <h3>Add Requirement</h3>
            <p>Create a new requirement for your team</p>
          </button>
          <Link to="/team/requirements" className="hr-dashboard-card">
            <h3>Team Workspace</h3>
            <p>Manage requirements and candidate progress</p>
          </Link>
          <Link to="/work/my-sourced-profiles" className="hr-dashboard-card">
            <h3>My Sourced Profiles</h3>
            <p>View profiles you and your team have sourced</p>
          </Link>
          <Link to="/reports/tracking" className="hr-dashboard-card">
            <h3>Status Tracking</h3>
            <p>Pipeline stages with candidate drill-down</p>
          </Link>
          <Link to="/work/today" className="hr-dashboard-card">
            <h3>Today Work</h3>
            <p>Monitor profiles that need follow-up today</p>
          </Link>
          <Link to="/work/interviews" className="hr-dashboard-card">
            <h3>Interviews</h3>
            <p>View and schedule upcoming interviews</p>
          </Link>
        </div>
      </section>

      <AppModal
        show={drillDownModal.show}
        onHide={closeDrillDownModal}
        title={drillDownModal.title}
        subtitle={`${drillDownModal.rows.length} record${drillDownModal.rows.length === 1 ? '' : 's'}`}
        size="xl"
        footer={(
          <Button variant="secondary" onClick={closeDrillDownModal}>
            Close
          </Button>
        )}
      >
        <DataTable
          columns={candidateColumns}
          data={drillDownModal.rows}
          rowKey="candidateId"
          hideToolbar
          searchPlaceholder={`Search ${drillDownModal.title.toLowerCase()}`}
          searchKeys={
            drillDownModal.showInterviewDate
              ? ['candidateName', 'client', 'role', 'regId', 'status', 'interviewDateLabel']
              : ['candidateName', 'client', 'role', 'regId', 'status']
          }
          emptyMessage={`No ${drillDownModal.title.toLowerCase()} records for the selected date range.`}
          pageSize={10}
        />
      </AppModal>
    </PageShell>
    </DashboardSourceProfileSearch>
  );
}

export default TeamLeadDashboard;
